import React, { useState } from 'react';
import Modal from './Modal';
import { cldThumb } from '../utils/img';
import { confirmDispatchRemoval, resolvePackIssues } from '../api/catalogOrders';

// Opens when Dispatch is refused (409) for a catalog reason. `issue` is the 409 detail:
//   booked_pieces_in_pack: pieces in this box are booked for another (catalog) order -> take them out.
//   pack_issues: a piece in this box went to a catalog order -> ignore it or replace it.
// Nothing is submitted until the user confirms a packer has already changed the box.
// onResolved() then retries the dispatch.
// cleanOnly: opened from the Clean pack button, so nothing is dispatched afterwards.
export default function DispatchIssueModal({ appt, issue, onClose, onResolved, cleanOnly = false }) {
  const isPackIssues = issue.code === 'pack_issues';
  const [choice, setChoice] = useState(() =>
    Object.fromEntries(issue.items.map(i => [i.item_id, { action: 'ignore', barcode: '' }])));
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');

  const set = (id, patch) => setChoice(c => ({ ...c, [id]: { ...c[id], ...patch } }));
  const missingBarcode = isPackIssues && issue.items.some(
    i => choice[i.item_id].action === 'replace' && !choice[i.item_id].barcode.trim());

  async function submit() {
    setBusy(true); setFailure(''); setErrors({});
    try {
      if (isPackIssues) {
        const res = await resolvePackIssues(appt.pack_id, issue.items.map(i => ({
          item_id: i.item_id, action: choice[i.item_id].action,
          replacement_barcode: choice[i.item_id].action === 'replace' ? choice[i.item_id].barcode.trim() : null,
        })));
        if (!res.ok) {
          const byItem = {};
          (res.errors || []).forEach(e => { byItem[e.item_id || e.zoddle_barcode] = e.message; });
          setErrors(byItem);
          return;
        }
      } else {
        await confirmDispatchRemoval(appt.pack_id);
      }
      await onResolved();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFailure(typeof d === 'string' ? d : d?.message || 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal wide title={isPackIssues ? (cleanOnly ? 'Clean this pack' : 'Pack issues — clean the box before dispatch') : 'Booked pieces in this box'}
      onClose={onClose}>
      <p className="text-sm text-gray-600 mb-4">
        {isPackIssues
          ? <>These pieces in <b>{appt.customer_name || 'this'}</b>’s box are booked for a catalog order. Take each one out
              (it goes back to a bin for that order), and either <b>ignore</b> it (send nothing in its place) or <b>replace</b> it
              with another piece from a bin. The catalog order can’t be packed until this is done.</>
          : <>These pieces are booked for another customer’s catalog order. Take them out of the box; they go back to a bin
              and stay booked for that order.</>}
      </p>

      <div className="space-y-2">
        {issue.items.map(i => {
          const c = choice[i.item_id];
          const err = errors[i.item_id] || errors[i.zoddle_barcode];
          return (
            <div key={i.item_id} className={`border rounded-xl p-3 ${err ? 'border-zoddle-pink' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                {i.image_url
                  ? <img src={cldThumb(i.image_url, 120)} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-100" />
                  : <div className="w-12 h-12 rounded-lg bg-gray-100" />}
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm font-semibold text-gray-800">{i.zoddle_barcode}</p>
                  {i.design_name && <p className="text-xs text-gray-500 truncate">{i.design_name}</p>}
                </div>
                {isPackIssues && (
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold shrink-0">
                    {['ignore', 'replace'].map(a => (
                      <button key={a} type="button" onClick={() => set(i.item_id, { action: a })}
                        className={`px-3 py-1.5 ${c.action === a ? 'bg-zoddle-green text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        {a === 'ignore' ? 'Ignore' : 'Replace'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {isPackIssues && c.action === 'replace' && (
                <input value={c.barcode} onChange={e => set(i.item_id, { barcode: e.target.value })}
                  placeholder="Scan or type the replacement barcode" autoFocus
                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
              )}
              {err && <p className="text-xs text-zoddle-pink mt-1.5">{err}</p>}
            </div>
          );
        })}
      </div>

      <label className="flex items-start gap-2 mt-4 text-sm text-gray-700 cursor-pointer">
        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-0.5" />
        <span>
          {isPackIssues
            ? 'A packer has taken these pieces out and put any replacements into the box.'
            : 'A packer has taken these pieces out of the box.'}
        </span>
      </label>

      {failure && <p className="text-sm text-zoddle-pink mt-3">{failure}</p>}

      <div className="flex gap-2 mt-4">
        <button onClick={onClose} className="flex-1 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={submit} disabled={!confirmed || missingBarcode || busy}
          className="flex-1 text-sm font-semibold text-white bg-zoddle-green rounded-lg px-3 py-2 disabled:opacity-40">
          {busy ? 'Saving…' : cleanOnly ? 'Confirm' : 'Confirm and dispatch'}
        </button>
      </div>
    </Modal>
  );
}
